import { AlertTriangle, Wrench } from "lucide-react";
import { formatPriceCents } from "@/components/services/service-meta";
import { cn } from "@/lib/cn";

type EquipamentoDaFicha = {
  id: string;
  name: string;
  totalCents: number;
  semPreco: string[];
  linhas: Array<{ id: string; nome: string; quantidadeBruta: number; custoCents: number | null; indireto: boolean }>;
};

/**
 * Que máquinas esta receita assume, e quanto elas somam.
 *
 * O custo é o consumível da máquina resolvido PARA O MATERIAL DESTA FICHA — a mesma fresadora
 * soma valores diferentes numa ficha de dissilicato e numa de resina. Por isso o valor aparece
 * aqui e não no cadastro do equipamento: sem saber o material, não há um número só.
 */
export function FichaTecnicaEquipamentos({
  disponiveis,
  escolhidos,
  materialOptionId,
  naFicha,
  onEscolher,
}: {
  disponiveis: Array<{
    id: string;
    name: string;
    active: boolean;
    /** Com que materiais a máquina trabalha. Vazio = trabalha com qualquer um. */
    materialOptionIds?: string[];
    custoPorMaterial?: Record<string, number>;
    semPrecoPorMaterial?: Record<string, number>;
  }>;
  escolhidos: string[];
  /** O material desta ficha — é ele que decide qual gasto da máquina vale. */
  materialOptionId: string | null;
  /** O que a ficha SALVA já traz, com as linhas detalhadas. O valor do chip não vem daqui: ele
   *  precisa aparecer no instante em que se marca a máquina, antes de salvar. */
  naFicha: EquipamentoDaFicha[];
  onEscolher: (ids: string[]) => void;
}) {
  /**
   * Só as máquinas que trabalham com o material desta ficha, mais as que não declaram material
   * nenhum (essas servem pra tudo). É a mesma regra que a produção já aplica em
   * `consumo.porServico` — oferecer a fresadora de zircônia numa ficha de resina é convite a
   * cadastrar gasto que nunca vai valer.
   *
   * Ficha sem material ("Padrão") não tem contra o que checar compatibilidade: aparecem todas.
   */
  const compativeis = disponiveis.filter(
    (e) =>
      !materialOptionId ||
      !e.materialOptionIds ||
      e.materialOptionIds.length === 0 ||
      e.materialOptionIds.includes(materialOptionId),
  );

  // Já marcada mas incompatível (o material da ficha mudou depois) continua aparecendo: sumir
  // esconderia um custo que está somando de verdade.
  const naTela = [
    ...compativeis,
    ...disponiveis.filter((e) => escolhidos.includes(e.id) && !compativeis.some((c) => c.id === e.id)),
  ];

  if (naTela.length === 0) return null;

  const alternar = (id: string) =>
    onEscolher(escolhidos.includes(id) ? escolhidos.filter((x) => x !== id) : [...escolhidos, id]);

  // Sem gasto declarado pro material desta ficha, vale o padrão — a mesma regra do servidor.
  const chave = materialOptionId ?? "__padrao__";
  const custoDe = (e: (typeof disponiveis)[number]) =>
    e.custoPorMaterial?.[chave] ?? e.custoPorMaterial?.__padrao__ ?? 0;
  const semPrecoDe = (e: (typeof disponiveis)[number]) =>
    e.semPrecoPorMaterial?.[chave] ?? e.semPrecoPorMaterial?.__padrao__ ?? 0;

  return (
    <div>
      <p className="mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground">
        Equipamentos
      </p>
      <div className="flex flex-wrap gap-1.5">
        {naTela.map((e) => {
          const ativo = escolhidos.includes(e.id);
          return (
            <button
              key={e.id}
              type="button"
              onClick={() => alternar(e.id)}
              className={cn(
                "flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-left text-xs transition-colors",
                ativo
                  ? "border-brand-300 bg-brand-50 font-semibold text-brand-700 dark:border-primary/40 dark:bg-primary/10 dark:text-primary"
                  : "border-zinc-200 bg-white text-zinc-600 hover:border-brand-300 dark:border-border dark:bg-card dark:text-muted-foreground",
                !e.active && "opacity-60",
              )}
            >
              <Wrench className="h-3.5 w-3.5 shrink-0 opacity-70" />
              <span className="truncate">{e.name}</span>
              {/* O custo aparece marcada ou não: é justamente ele que decide se vale marcar. */}
              <span className="shrink-0 text-[10.5px] tabular-nums opacity-80">
                {formatPriceCents(custoDe(e))}
              </span>
              {semPrecoDe(e) > 0 && <AlertTriangle className="h-3 w-3 shrink-0 text-amber-500" />}
            </button>
          );
        })}
      </div>

      {naFicha.some((e) => escolhidos.includes(e.id) && e.linhas.length > 0) && (
        <div className="mt-1.5 rounded-md border border-zinc-100 p-2 dark:border-border/60">
          {naFicha
            .filter((e) => escolhidos.includes(e.id))
            .map((e) => (
              <div key={e.id} className="mb-1 last:mb-0">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 dark:text-muted-foreground/70">
                  {e.name}
                </p>
                {e.linhas.length === 0 ? (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400">
                    Sem gasto cadastrado para este material.
                  </p>
                ) : (
                  e.linhas.map((l) => (
                    <p
                      key={l.id}
                      className="flex items-baseline justify-between gap-2 text-[11px] text-zinc-600 dark:text-muted-foreground"
                    >
                      <span className="truncate">
                        {l.nome}
                        {l.indireto && <span className="ml-1 text-[9.5px] uppercase">indireto</span>}
                      </span>
                      <span className="shrink-0 tabular-nums">
                        {l.quantidadeBruta.toLocaleString("pt-BR", { maximumFractionDigits: 4 })}
                        {l.custoCents != null && ` · ${formatPriceCents(Math.round(l.custoCents))}`}
                      </span>
                    </p>
                  ))
                )}
              </div>
            ))}
        </div>
      )}
      <p className="mt-1 text-[11px] leading-snug text-zinc-500 dark:text-muted-foreground">
        O insumo que cada máquina gasta já está somado no custo abaixo, e sai do estoque quando
        o trabalho concluir.
      </p>
    </div>
  );
}
